import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { Check, ChevronDown } from 'lucide-react';

import { SurveyBanner } from './SurveyBanner';
import { SurveyOptionIcon, hasSurveyOptionIcon } from './SurveyOptionIcon';
import { useOnboarding } from '../context';
import { optionLabelParams, otherKey } from '../survey';

import type { OnboardingAnswer } from '../flow';
import type { SurveyStepConfig } from '../survey';

/**
 * 설문 단계 화면 — 설정(SurveyStepConfig)에 따라 단일/다중 선택 옵션을 렌더하고
 * 응답을 컨텍스트에 기록한다. otherValue 옵션 선택 시 자유입력 텍스트를 노출한다.
 * 문구는 전부 i18n 키(config)에서 나오고, 제목은 앞서 받은 닉네임을 부른다.
 * CTA 활성화 판정은 상위(OnboardingPage)가 isSurveyStepAnswered로 한다.
 *
 * **배치는 시안 §2다**: 제목 → 안내 배너 → **여백** → 선택지 목록(하단 정렬).
 * 선택지를 제목 바로 밑에 붙이면 화면마다 목록의 세로 위치가 달라져(선택지 3개와 5개)
 * 연속으로 답할 때 손가락이 매번 다른 곳을 찾는다. 하단 정렬은 그 자리를 고정한다.
 *
 * 부제는 **배너**로 낸다 — 시안의 이 문장들은 전부 "왜 묻는지"를 설명하는 안내이고
 * (`성별에 따라 음역·공명 기준이 달라서 필요해요.`), 회색 소제목으로 두면 제목의 연장선
 * 처럼 읽혀 그 역할이 사라진다.
 */

const toggleMulti = (
  current: OnboardingAnswer | undefined,
  value: string,
): string[] => {
  const list = Array.isArray(current) ? current : [];
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
};

export const SurveyStep = ({ config }: { config: SurveyStepConfig }) => {
  const { t } = useTranslation();
  const { answers, answer } = useOnboarding();
  const value = answers[config.id];
  // 닉네임은 제목 안에서 불린다("<닉네임>님의 나이를…"). 아직(또는 구 드래프트라)
  // 이름이 없으면 문장이 깨지지 않게 대체 호칭으로 부른다.
  const nick =
    typeof answers.nick === 'string' && answers.nick.trim().length > 0
      ? answers.nick.trim()
      : t('web.onboarding.nick.fallback');

  const isSelected = (v: string): boolean =>
    config.kind === 'single'
      ? value === v
      : Array.isArray(value) && value.includes(v);

  const select = (v: string): void => {
    const nextValue = config.kind === 'single' ? v : toggleMulti(value, v);
    answer(config.id, nextValue);
    // 'other'가 선택에서 빠지면 자유입력 텍스트도 비워 stale 값이 저장되지 않게 한다.
    const stillOther =
      config.kind === 'single'
        ? nextValue === config.otherValue
        : Array.isArray(nextValue) &&
          nextValue.includes(config.otherValue ?? '');
    if (config.otherValue && !stillOther && answers[otherKey(config.id)]) {
      answer(otherKey(config.id), '');
    }
  };

  const otherActive =
    config.otherValue !== undefined && isSelected(config.otherValue);
  const otherText = answers[otherKey(config.id)];

  return (
    <div className="flex flex-1 flex-col">
      <h2 className="whitespace-pre-line text-[22px] font-semibold leading-[1.5] tracking-[-0.11px] text-[hsl(var(--title))]">
        {t(config.titleKey, { nick })}
      </h2>

      {config.subtitleKey && (
        <SurveyBanner className="mt-4">
          {t(config.subtitleKey, { nick })}
        </SurveyBanner>
      )}

      {/* 목록을 화면 아래로 미는 여백. 시안이 선택지를 하단 정렬한다. */}
      <div className="min-h-6 flex-1" aria-hidden="true" />

      {config.kind === 'year' ? (
        // 연도는 70개 — 버튼으로 깔면 화면을 다 먹는다. 기본 선택은 두지 않는다:
        // 미리 채우면 답하지 않은 사용자도 결과에 '실제 나이'가 찍힌다.
        // `appearance-none`은 필수다 — iOS 18 WebKit은 네이티브 모양의 `<select>`에서
        // 세로 패딩을 0으로 눌러 60px 필드가 29px 알약이 된다(실기기·시뮬레이터 실측).
        // 네이티브 화살표도 같이 사라지므로 셰브론을 직접 얹는다.
        <div className="relative">
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => answer(config.id, e.target.value)}
            aria-label={t(config.titleKey, { nick })}
            className="w-full appearance-none rounded-2xl border-2 border-[hsl(var(--field-border))] bg-[hsl(var(--surface-lowest))] py-4 pl-4 pr-12 text-base text-foreground"
          >
            <option value="" disabled>
              {config.placeholderKey && t(config.placeholderKey)}
            </option>
            {config.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey, optionLabelParams(config, opt))}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-bk-600"
            aria-hidden="true"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {config.options.map((opt) => {
            const selected = isSelected(opt.value);
            const withIcon = hasSurveyOptionIcon(config.id, opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={selected}
                onClick={() => select(opt.value)}
                // 시안 `758:20593`: 라운드 20 · 테두리 2px(선택 시 레드 48%) · 타일이 있으면
                // 안쪽 여백 10px, 글자만이면 좌우 18px · 라벨 16px Bold.
                className={cn(
                  'flex min-h-[76px] items-center justify-between rounded-[20px] border-2 bg-[hsl(var(--surface-lowest))] py-2.5 pr-[18px] text-left text-base font-bold tracking-[-0.5px] text-bk-900 transition-colors',
                  withIcon ? 'pl-2.5' : 'pl-[18px]',
                  selected
                    ? 'border-[rgba(251,35,59,0.48)]'
                    : 'border-[hsl(var(--field-border))]',
                )}
              >
                {/* 아이콘이 있는 단계는 타일 + 라벨이 한 덩어리로 왼쪽에 선다.
                    없는 단계(워너비·목적·컨디션)는 시안도 글자만이라 자리를 안 만든다. */}
                {withIcon ? (
                  <span className="flex items-center gap-3">
                    <SurveyOptionIcon stepId={config.id} value={opt.value} />
                    <span>
                      {t(opt.labelKey, optionLabelParams(config, opt))}
                    </span>
                  </span>
                ) : (
                  <span>{t(opt.labelKey, optionLabelParams(config, opt))}</span>
                )}
                {/* 체크 배지 — 테두리 색만으로는 어느 걸 골랐는지 흑백 화면·색약에서
                    안 읽힌다. 시안도 연분홍 원(레드 16%) + 레드 체크 20px을 같이 둔다. */}
                {selected && (
                  <span
                    data-testid="survey-option-check"
                    aria-hidden="true"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(251,35,59,0.16)]"
                  >
                    <Check className="h-5 w-5 text-primary" strokeWidth={2.5} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {otherActive && (
        <input
          type="text"
          value={typeof otherText === 'string' ? otherText : ''}
          onChange={(e) => answer(otherKey(config.id), e.target.value)}
          placeholder={t('web.onboarding.other.placeholder')}
          aria-label={t('web.onboarding.other.label')}
          className="mt-3 rounded-2xl border-2 border-[hsl(var(--field-border))] bg-[hsl(var(--surface-lowest))] px-4 py-3.5 text-foreground placeholder:text-[hsl(var(--placeholder))]"
        />
      )}
    </div>
  );
};
